import React from 'react';
import { ScreenProps } from '../App';
import { formatCurrency } from '../utils/format';
import { useCommaInput } from '../utils/useCommaInput';

const fmt = formatCurrency;

const InputField: React.FC<{label: string; value: number; onChange: (v: number) => void; hint?: string; memo?: boolean}> = ({label, value, onChange, hint, memo}) => {
  const input = useCommaInput(value, onChange);
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">{label}</label>
      {hint && <div className="text-xs text-gray-400 mb-1">{hint}</div>}
      <div className="relative">
        <span className="absolute left-3 top-2 text-gray-400 text-sm">£</span>
        <input {...input} placeholder="0"
          className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 ${memo ? 'border-dashed border-gray-300 bg-gray-50' : 'border-gray-200 bg-white'}`} />
      </div>
    </div>
  );
};

const MonthlyWaterfall: React.FC<ScreenProps> = ({ data, setData }) => {
  const update = (key: keyof typeof data) => (value: number) => setData(prev => ({ ...prev, [key]: value }));

  // Net monthly from income screen
  const otherIncome = (data.payeIncome || 0) + (data.rentalIncomeNet || 0) + (data.savingsInterest || 0) + (data.otherDividends || 0);
  const grossMonthly = (data.salary + data.dividends + otherIncome) / 12 + data.expensesReimbursement;
  const dividendTaxMonthly = Math.max(0, data.dividends - 500) * 0.1075 / 12;
  const incomeTaxMonthly = Math.max(0, data.salary - 12570) * 0.20 / 12;
  const niMonthly = Math.min(Math.max(0, data.salary - 12570), 37700) * 0.08 / 12;
  const taxMonthly = data.taxPaymentMethod === 'monthly' ? data.monthlyHMRC : (dividendTaxMonthly + incomeTaxMonthly + niMonthly);
  const netMonthly = grossMonthly - taxMonthly;

  const debtMinPayments = data.consumerDebtMonthlyPayment || 0;
  const committed = data.mortgagePayment + data.mortgageOP + data.isaContribution + data.efTopUp + data.sinkingFundsTotal + debtMinPayments;
  const surplus = netMonthly - committed;
  const mortgagePct = netMonthly > 0 ? (data.mortgagePayment / netMonthly) * 100 : 0;
  const mortgageWithOPPct = netMonthly > 0 ? ((data.mortgagePayment + data.mortgageOP) / netMonthly) * 100 : 0;
  // investedPct available for future use
  // const investedPct = (data.salary + data.dividends + data.otherIncome) > 0
  //   ? ((data.isaContribution + data.pensionMonthly) / ((data.salary + data.dividends + data.otherIncome) / 12)) * 100 : 0;

  const waterfallItems = [
    { label: 'Mortgage payment', value: data.mortgagePayment, color: '#EF9F27', deduction: true },
    { label: 'Mortgage overpayment', value: data.mortgageOP, color: '#FAC775', deduction: true },
    { label: 'Consumer debt minimums', value: debtMinPayments, color: '#F87171', deduction: true },
    { label: 'ISA contribution', value: data.isaContribution, color: '#85B7EB', deduction: true },
    { label: 'Emergency fund top-up', value: data.efTopUp, color: '#C0DD97', deduction: true },
    { label: 'Sinking funds', value: data.sinkingFundsTotal, color: '#9FE1CB', deduction: true },
  ].filter(i => i.value > 0);

  return (
    <div className="flex h-full">
      {/* LEFT */}
      <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 p-6 overflow-y-auto">
        <div className="bg-blue-50 rounded-lg p-3 mb-4">
          <div className="text-xs text-blue-600 font-medium">Net monthly available</div>
          <div className="text-xl font-bold text-ma-navy">{fmt(netMonthly)}</div>
          <div className="text-xs text-blue-400">From income screen</div>
        </div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Committed Outgoings</div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Mortgage payment (£/mo)</label>
          {data.mortgagePayment > 0 ? (
            <div className="text-sm font-semibold text-ma-navy bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
              {fmt(data.mortgagePayment)}/mo <span className="text-xs text-gray-400 font-normal">from Assets</span>
            </div>
          ) : (
            <div className="text-xs text-gray-400 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
              Enter mortgage details in Assets to calculate payment.
            </div>
          )}
        </div>
        <InputField label="Mortgage overpayment SO (£/mo)" value={data.mortgageOP} onChange={update('mortgageOP')} />
        {debtMinPayments > 0 && (
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Consumer debt minimums (£/mo)</label>
            <div className="text-sm font-semibold text-ma-navy bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
              {fmt(debtMinPayments)}/mo <span className="text-xs text-gray-400 font-normal">from Debts</span>
            </div>
          </div>
        )}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">ISA standing order (£/mo)</label>
          <div className="text-sm font-semibold text-ma-navy bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
            {fmt(data.isaContribution)}/mo <span className="text-xs text-gray-400 font-normal">from Assets</span>
          </div>
        </div>
        <InputField label="Emergency fund top-up (£/mo)" value={data.efTopUp} onChange={update('efTopUp')} />
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Sinking funds total (£/mo)</label>
          <div className="text-sm text-gray-400 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
            {data.sinkingFundsTotal > 0 ? `${fmt(data.sinkingFundsTotal)}/mo` : '£0'}
            <span className="text-xs text-gray-400 ml-1">— Introduced after Session 1 — see Three Actions</span>
          </div>
        </div>
        <div className="border-t border-gray-100 my-4"></div>
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pension (Memo)</div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Personal pension (£/mo)</label>
          <div className="text-sm font-semibold text-ma-navy bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
            {fmt(data.pensionMonthly)}/mo <span className="text-xs text-gray-400 font-normal">from Assets</span>
          </div>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Company-funded pension (£/mo)</label>
          <div className="text-sm font-semibold text-ma-navy bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">
            {fmt(data.pensionCompanyFunded)}/mo <span className="text-xs text-gray-400 font-normal">from Assets</span>
          </div>
          {data.pensionCompanyFunded > 0 && <div className="text-xs text-green-500 mt-0.5">saves CT on full amount</div>}
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex-1 p-8 overflow-y-auto bg-gray-50">
        <div className="max-w-2xl">
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Net available', value: fmt(netMonthly), sub: 'per month' },
              { label: 'Committed', value: fmt(committed), sub: fmt(committed * 12) + '/yr' },
              { label: 'Monthly surplus', value: fmt(surplus), sub: 'lifestyle + investments', highlight: surplus > 0 },
              { label: 'Mortgage vs net', value: mortgagePct.toFixed(1) + '%', sub: mortgageWithOPPct.toFixed(1) + '% incl. OP', warning: mortgagePct > 25 },
            ].map(c => (
              <div key={c.label} className={`rounded-xl p-4 border ${(c as any).highlight ? 'bg-ma-navy text-white border-transparent' : (c as any).warning ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
                <div className={`text-xs uppercase tracking-wide mb-1 ${(c as any).highlight ? 'text-blue-200' : 'text-gray-400'}`}>{c.label}</div>
                <div className={`text-lg font-semibold ${(c as any).highlight ? 'text-white' : (c as any).warning ? 'text-amber-700' : 'text-gray-800'}`}>{c.value}</div>
                <div className={`text-xs mt-0.5 ${(c as any).highlight ? 'text-blue-200' : 'text-gray-400'}`}>{c.sub}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
            <div className="text-sm font-semibold text-gray-700 mb-5">Monthly waterfall</div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-40 text-xs text-gray-500 text-right">Net monthly</div>
              <div className="flex-1 h-8 bg-ma-navy rounded-md flex items-center px-3">
                <span className="text-white text-sm font-semibold">{fmt(netMonthly)}</span>
              </div>
              <div className="w-24 text-xs font-medium text-green-700 text-right">+{fmt(netMonthly)}</div>
            </div>
            {waterfallItems.map((item, i) => {
              const pct = netMonthly > 0 ? (item.value / netMonthly) * 100 : 0;
              return (
                <div key={i} className="flex items-center gap-3 mb-2">
                  <div className="w-40 text-xs text-gray-500 text-right">{item.label}</div>
                  <div className="flex-1 h-7 bg-gray-100 rounded-md overflow-hidden relative">
                    <div className="absolute top-0 right-0 h-full rounded-md flex items-center px-2"
                      style={{ width: Math.max(pct, 2) + '%', backgroundColor: item.color }}>
                      {pct > 10 && <span className="text-xs font-medium text-white">{fmt(item.value)}</span>}
                    </div>
                  </div>
                  <div className="w-24 text-xs font-medium text-red-500 text-right">−{fmt(item.value)}</div>
                </div>
              );
            })}
            {data.pensionMonthly > 0 && (
              <div className="flex items-center gap-3 mb-2 opacity-60">
                <div className="w-40 text-xs text-gray-400 text-right italic">Pension (memo)</div>
                <div className="flex-1 h-7 bg-gray-50 rounded-md border border-dashed border-gray-300 flex items-center px-3">
                  <span className="text-xs text-gray-400">{fmt(data.pensionMonthly)}/mo — company funded</span>
                </div>
                <div className="w-24 text-xs text-purple-400 text-right">{fmt(data.pensionMonthly)}*</div>
              </div>
            )}
            <div className="border-t border-gray-200 mt-4 pt-4 flex items-center gap-3">
              <div className="w-40 text-xs font-semibold text-gray-700 text-right">Surplus</div>
              <div className={`flex-1 h-8 rounded-md flex items-center px-3 ${surplus >= 0 ? 'bg-blue-600' : 'bg-red-500'}`}>
                <span className="text-white text-sm font-semibold">{fmt(surplus)}/month</span>
              </div>
              <div className={`w-24 text-sm font-semibold text-right ${surplus >= 0 ? 'text-blue-600' : 'text-red-500'}`}>{fmt(surplus)}</div>
            </div>
          </div>

          {mortgagePct > 0 && (
            <div className={`rounded-xl p-4 border text-sm ${mortgagePct <= 25 ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              {mortgagePct <= 25
                ? `✓ Mortgage at ${mortgagePct.toFixed(1)}% of net — within Ramsey's 25% guideline`
                : `Mortgage at ${mortgagePct.toFixed(1)}% of net — above Ramsey's 25% ceiling (${fmt(netMonthly * 0.25)}/mo target)`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyWaterfall;
